import { emitEvent, isTMA, mockTelegramEnv } from '@tma.js/sdk-react';

// Ключи для сохранения в localStorage
const STORAGE_KEYS = {
  LAUNCH_PARAMS: 'tg_launch_params',
  THEME_PARAMS: 'tg_theme_params',
  USER_DATA: 'tg_user_data',
  ORIGINAL_WEB_APP_DATA: 'tg_original_web_app_data', // Новый ключ для оригинальных данных
} as const;

// Интерфейсы для типизации
interface ThemeParams {
  accent_text_color: string;
  bg_color: string;
  button_color: string;
  button_text_color: string;
  destructive_text_color: string;
  header_bg_color: string;
  hint_color: string;
  link_color: string;
  secondary_bg_color: string;
  section_bg_color: string;
  section_header_text_color: string;
  subtitle_text_color: string;
  text_color: string;
}

interface UserData {
  id: number;
  first_name: string;
  last_name?: string;
  username?: string;
  language_code?: string;
}

interface LaunchParamsData {
  version: string;
  platform: string;
  hasValidData: boolean;
}

/**
 * Извлекает параметры из URL
 */
function extractUrlParams(): URLSearchParams | null {
  if (typeof window === 'undefined') {
    return null;
  }

  const urlParams = new URLSearchParams(window.location.search);
  const hashParams = new URLSearchParams(window.location.hash.slice(1));

  // Объединяем параметры из search и hash
  const combinedParams = new URLSearchParams();

  urlParams.forEach((value, key) => {
    combinedParams.set(key, value);
  });

  hashParams.forEach((value, key) => {
    combinedParams.set(key, value);
  });

  return combinedParams.toString() ? combinedParams : null;
}

/**
 * Сохраняет параметры в localStorage
 */
function saveToStorage(key: string, data: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(data));
    console.debug(`✅ Saved ${key} to localStorage`);
  } catch (error) {
    console.warn(`❌ Failed to save ${key} to localStorage:`, error);
  }
}

/**
 * Загружает параметры из localStorage
 */
function loadFromStorage<T>(key: string): T | null {
  try {
    const stored = localStorage.getItem(key);
    const result = stored ? JSON.parse(stored) : null;
    if (result) {
      console.debug(`✅ Loaded ${key} from localStorage`);
    }
    return result;
  } catch (error) {
    console.warn(`❌ Failed to load ${key} from localStorage:`, error);
    return null;
  }
}

/**
 * Валидирует tgWebAppData (проверяет наличие обязательных полей)
 */
function validateTgWebAppData(tgWebAppData: string): boolean {
  try {
    const params = new URLSearchParams(tgWebAppData);
    const hash = params.get('hash');
    const authDate = params.get('auth_date');
    const userStr = params.get('user');

    if (!hash || !authDate || !userStr) {
      console.warn('Missing required fields in tgWebAppData:', {
        hash: !!hash,
        authDate: !!authDate,
        user: !!userStr,
      });
      return false;
    }

    // Проверяем что user валидный JSON
    const user = JSON.parse(userStr);
    if (!user || !user.id) {
      console.warn('Invalid user data in tgWebAppData');
      return false;
    }

    return true;
  } catch (error) {
    console.warn('Failed to validate tgWebAppData:', error);
    return false;
  }
}

/**
 * Извлекает пользователя из tgWebAppData
 */
function extractUserFromWebAppData(tgWebAppData: string): UserData | null {
  try {
    const params = new URLSearchParams(tgWebAppData);
    const userStr = params.get('user');
    if (!userStr) {
      return null;
    }

    return JSON.parse(userStr);
  } catch (error) {
    console.warn('Failed to extract user from tgWebAppData:', error);
    return null;
  }
}

/**
 * Получает или создает параметры запуска
 */
function getLaunchParams(): URLSearchParams | null {
  const urlParams = extractUrlParams();

  let themeParams: ThemeParams | null = null;
  let userData: UserData | null = null;
  let tgWebAppData: string | null = null;
  let version = '8.4';
  let platform = 'tdesktop';

  if (urlParams) {
    // Извлекаем и валидируем theme params
    const tgWebAppThemeParams = urlParams.get('tgWebAppThemeParams');
    if (tgWebAppThemeParams) {
      try {
        themeParams = JSON.parse(tgWebAppThemeParams);
        // Сохраняем тему сразу при получении из URL
        saveToStorage(STORAGE_KEYS.THEME_PARAMS, themeParams);
      } catch (error) {
        console.warn('Failed to parse theme params from URL:', error);
        // Fallback на сохраненные данные
        themeParams = loadFromStorage<ThemeParams>(STORAGE_KEYS.THEME_PARAMS);
      }
    } else {
      // Загружаем сохраненную тему
      themeParams = loadFromStorage<ThemeParams>(STORAGE_KEYS.THEME_PARAMS);
    }

    // Извлекаем и валидируем web app data
    const rawTgWebAppData = urlParams.get('tgWebAppData');
    if (rawTgWebAppData && validateTgWebAppData(rawTgWebAppData)) {
      // ✅ Сохраняем ОРИГИНАЛЬНУЮ строку без изменений
      tgWebAppData = rawTgWebAppData;
      userData = extractUserFromWebAppData(tgWebAppData);

      // Сохраняем оригинальные данные
      saveToStorage(STORAGE_KEYS.ORIGINAL_WEB_APP_DATA, tgWebAppData);
      if (userData) {
        saveToStorage(STORAGE_KEYS.USER_DATA, userData);
      }
    } else if (rawTgWebAppData) {
      console.warn(
        'Invalid tgWebAppData from URL, falling back to stored data',
      );
      // Fallback на сохраненные данные
      tgWebAppData = loadFromStorage<string>(
        STORAGE_KEYS.ORIGINAL_WEB_APP_DATA,
      );
      userData = loadFromStorage<UserData>(STORAGE_KEYS.USER_DATA);
    } else {
      // Загружаем сохраненные данные
      tgWebAppData = loadFromStorage<string>(
        STORAGE_KEYS.ORIGINAL_WEB_APP_DATA,
      );
      userData = loadFromStorage<UserData>(STORAGE_KEYS.USER_DATA);
    }

    // Извлекаем версию и платформу
    version = urlParams.get('tgWebAppVersion') || version;
    platform = urlParams.get('tgWebAppPlatform') || platform;
  } else {
    // Нет параметров в URL - загружаем из localStorage

    themeParams = loadFromStorage<ThemeParams>(STORAGE_KEYS.THEME_PARAMS);
    userData = loadFromStorage<UserData>(STORAGE_KEYS.USER_DATA);
    tgWebAppData = loadFromStorage<string>(STORAGE_KEYS.ORIGINAL_WEB_APP_DATA);

    const storedLaunchParams = loadFromStorage<LaunchParamsData>(
      STORAGE_KEYS.LAUNCH_PARAMS,
    );
    if (storedLaunchParams) {
      version = storedLaunchParams.version || version;
      platform = storedLaunchParams.platform || platform;
    }
  }

  // Дополнительная валидация сохраненных данных
  if (tgWebAppData && !validateTgWebAppData(tgWebAppData)) {
    tgWebAppData = null;
    userData = null;
    // Очищаем некорректные данные
    localStorage.removeItem(STORAGE_KEYS.ORIGINAL_WEB_APP_DATA);
    localStorage.removeItem(STORAGE_KEYS.USER_DATA);
  }

  // Сохраняем метаданные
  const launchParamsData: LaunchParamsData = {
    version,
    platform,
    hasValidData: !!(themeParams && tgWebAppData),
  };
  saveToStorage(STORAGE_KEYS.LAUNCH_PARAMS, launchParamsData);

  // Проверяем минимальные требования для мока
  if (!themeParams || !tgWebAppData) {
    console.warn('❌ Insufficient data for Telegram mock:', {
      hasTheme: !!themeParams,
      hasWebAppData: !!tgWebAppData,
      hasUser: !!userData,
    });
    return null;
  }

  // Создаем финальные параметры запуска
  const resultParams = new URLSearchParams([
    ['tgWebAppThemeParams', JSON.stringify(themeParams)],
    ['tgWebAppData', tgWebAppData], // ✅ Используем оригинальную строку
    ['tgWebAppVersion', version],
    ['tgWebAppPlatform', platform],
  ]);

  return resultParams;
}

/**
 * Очищает сохраненные данные (для разработки)
 */
export function clearTelegramStorage(): void {
  Object.values(STORAGE_KEYS).forEach((key) => {
    localStorage.removeItem(key);
  });
}

/**
 * Тестирует валидность текущих данных
 */
export function testTelegramDataValidity(): boolean {
  const tgWebAppData = loadFromStorage<string>(
    STORAGE_KEYS.ORIGINAL_WEB_APP_DATA,
  );

  if (!tgWebAppData) {
    console.error('❌ No tgWebAppData found in storage');
    return false;
  }

  const isValid = validateTgWebAppData(tgWebAppData);

  return isValid;
}

/**
 * Мокает данные Telegram Mini App с поддержкой URL и localStorage
 */
export async function mockData(): Promise<void> {
  return isTMA('complete').then((isTma) => {
    if (!isTma) {
      const launchParams = getLaunchParams();

      if (!launchParams) {
        console.warn('❌ No valid Telegram data found.');
        console.info('💡 Use debugTelegramStorage() to see current state');
        console.info(
          '💡 Use testTelegramDataValidity() to check data validity',
        );
        return;
      }

      const themeParamsStr = launchParams.get('tgWebAppThemeParams');
      const themeParams = themeParamsStr ? JSON.parse(themeParamsStr) : {};
      const noInsets = { left: 0, top: 0, bottom: 0, right: 0 } as const;

      mockTelegramEnv({
        onEvent(e) {
          if (e.name === 'web_app_request_theme') {
            return emitEvent('theme_changed', {
              theme_params: themeParams,
            });
          }
          if (e.name === 'web_app_request_viewport') {
            return emitEvent('viewport_changed', {
              height: window.innerHeight,
              width: window.innerWidth,
              is_expanded: true,
              is_state_stable: true,
            });
          }
          if (e.name === 'web_app_request_content_safe_area') {
            return emitEvent('content_safe_area_changed', noInsets);
          }
          if (e.name === 'web_app_request_safe_area') {
            return emitEvent('safe_area_changed', noInsets);
          }
        },
        launchParams,
      });

      // Дополнительная проверка
      const isDataValid = testTelegramDataValidity();
      if (!isDataValid) {
        console.warn('⚠️ Mocked data may not pass Telegram validation');
      }
    }
  });
}

export type { LaunchParamsData, ThemeParams, UserData };
